
import {throwError as observableThrowError, of as observableOf,  Observable ,  Subject, throwError } from 'rxjs';

import {catchError, tap} from 'rxjs/operators';
//taken from https://hackernoon.com/angular-simple-in-memory-cache-service-on-the-ui-with-rxjs-77f167387e39
//changed to use session storage

import { Injectable } from '@angular/core';
import { Constants } from 'app/utility/constants';

interface CacheContent {
    expiry: number;
    value: any;
}


/**
 * Cache Service is an observables based in-memory cache implementation
 * Keeps track of in-flight observables and sets a default expiry for cached values
 * @export
 * @class CacheService
 */
@Injectable()
export class CacheService {
    constructor() {
    }

    private inFlightObservables: Map<string, Subject<any>> = new Map<string, Subject<any>>();
    readonly DEFAULT_MAX_AGE: number = 300000;

    private formatKey(key: string): string {
        var username = localStorage.getItem(Constants.LocalStorage_MCUsername);
		var customerNumber = localStorage.getItem(Constants.LocalStorage_MCCustomerNumber);
		var formattedKey =  customerNumber+"-"+username+"-"+key;
		// console.log("CacheService cachekey is ", formattedKey);
        return formattedKey;
    }

    /**
     * Gets the value from cache if the key is provided.
     * If no value exists in cache, then check if the same call exists
     * in flight, if so return the subject. If not create a new
     * Subject inFlightObservable and return the source observable.
     */
    get(key: string, fallback?: Observable<any>, maxAge?: number, retryIfLengthIsZero: boolean = true): Observable<any> | Subject<any> {
        let formattedKey = this.formatKey(key);
        // console.log(`%ccache.service.ts get: ${formattedKey}`, 'color: blue');
        if (formattedKey && this.hasValidCachedValue(formattedKey)) {
            // console.log(`%cGetting from cache ${formattedKey}`, 'color: green');
            var value = this.getFromCache(formattedKey).value;
            // console.log (value);
            if (retryIfLengthIsZero && (!value || value.length==0)) {
                //don't return
                // console.log("Cached list is empty, so fetching from API");
            }
            else {
                return observableOf(value);
            }
        }

        if (!maxAge) {
            maxAge = this.DEFAULT_MAX_AGE;
        }

        if (this.inFlightObservables.has(formattedKey)) {
            return this.inFlightObservables.get(formattedKey);
        } else if (fallback && fallback instanceof Observable) {
            this.inFlightObservables.set(formattedKey, new Subject());
            // console.log(`%c Calling api for ${formattedKey}`, 'color: purple');
            return fallback.pipe(
                tap((value) => { this.set(key, value, maxAge); }),
                catchError((e: any) => { // `fallback` are crashed
                    // crashed flight is terrible😰, it's better to clean it up...
                    //this.inFlightObservables.delete(formattedKey);
                    // and when we have done our job, it's good idea to let the others know this event.
                    // maybe they have their stuffs need to be done too.
                    this.notifyInFlightObserversErrors(key, e);
                    return throwError(e);
            })
        );
        } else {
            return throwError('Requested key is not available in Cache');
        }
        //Not currently working (14/10/2019)
        // if (formattedKey && this.inFlightObservables.has(formattedKey)) {
        //     console.log(`%cinflightObservables.get ${formattedKey}`, 'color: orange');
        //     return this.inFlightObservables.get(formattedKey);
        // }
        // else
        if (fallback && fallback instanceof Observable) {
            if (formattedKey) {
                this.inFlightObservables.set(formattedKey, new Subject());
                // console.log(`%cCalling api for ${formattedKey}`, 'color: purple');
            }
            return fallback.pipe(tap((value) => {
                this.set(key, value, maxAge);
            }));
        } else {
            return observableThrowError('Requested key is not available in Cache');
        }

    }

    private getFromCache(formattedKey: string): any {
        if (!formattedKey) {
            return null;
        }

        var itemStr = sessionStorage.getItem(formattedKey);
        var item = null;
        if (itemStr && itemStr.length > 0) {
            try {
                item = JSON.parse(itemStr);
            }
            catch (e) {
                item = null;
            }
        }
        return item;
    }

    /**
     * Sets the value with key in the cache
     * Notifies all observers of the new value
     */
    set(key: string, value: any, maxAge: number = this.DEFAULT_MAX_AGE): void {
        let formattedKey = this.formatKey(key);
        if (formattedKey) {
            //this.cache.set(key, { value: value, expiry: Date.now() + maxAge });
            // console.log(`%cSetting value for ${formattedKey}`, 'color: pink');
            sessionStorage.setItem(formattedKey, JSON.stringify({ value: value, expiry: Date.now() + maxAge }));
            this.notifyInFlightObservers(key, value);
        }
    }

    /**
     * Checks if the a key exists in cache
     */
    has(key: string): boolean {
        let formattedKey = this.formatKey(key);
        //return this.cache.has(key);
        return (!!sessionStorage.getItem(formattedKey));
    }

    /**
     * Removes cache item for given key if it exists
     */
    clear(key: string): void {
        let formattedKey = this.formatKey(key);
		console.debug('Clear cached item for key:', formattedKey);
        sessionStorage.removeItem(formattedKey);
    }

    /**
     * Publishes the value to all observers of the given
     * in progress observables if observers exist.
     */
    private notifyInFlightObservers(key: string, value: any): void {
        let formattedKey = this.formatKey(key);
        if (!formattedKey) {
            return;
        }

        if (this.inFlightObservables.has(formattedKey)) {
            const inFlight = this.inFlightObservables.get(formattedKey);
            const observersCount = inFlight.observers.length;
            if (observersCount) {
                // console.log(`%cNotifying ${inFlight.observers.length} flight subscribers for ${formattedKey}`, 'color: blue');
                inFlight.next(value);
            }
            inFlight.complete();
            this.inFlightObservables.delete(formattedKey);
        }
    }

    private notifyInFlightObserversErrors(key: string, value: any): void {
        let formattedKey = this.formatKey(key);
        if (!formattedKey) {
            return;
        }

        if (this.inFlightObservables.has(formattedKey)) {
            const inFlight = this.inFlightObservables.get(formattedKey);
            const observersCount = inFlight.observers.length;
            if (observersCount) {
                // console.log(`%cNotifying ${inFlight.observers.length} flight error subscribers for ${formattedKey}`, 'color: blue');
                inFlight.error(value);
            }
            inFlight.complete();
            this.inFlightObservables.delete(formattedKey);
        }
    }

    /**
     * Checks if the key exists and has not expired.
     */
    private hasValidCachedValue(formattedKey: string): boolean {
        if (!formattedKey) {
            return false;
        }

        var item = this.getFromCache(formattedKey);

        if (item) {
            if (item.expiry < Date.now()) {
                sessionStorage.removeItem(formattedKey);
                return false;
            }
            return true;
        } else {
            return false;
        }
    }
}
